import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicRegister = ({ onBackToLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    codice_fiscale: '',
    figura_professionale: '',
    asl_riferimento: 'Asl Pescara',
    privacy_consent: false,
    privacy_consent_date: null,
    privacy_consent_version: null
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: 'gray' });
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchPrivacyPolicy();
  }, [fetchSettings, fetchPrivacyPolicy]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      console.log('Public settings loaded:', response.data);
      setSettings(response.data);
      if (response.data.figure_professionali && response.data.figure_professionali.length > 0) {
        setFormData(prev => ({ ...prev, figura_professionale: response.data.figure_professionali[0].nome }));
        console.log('Default figura set to:', response.data.figure_professionali[0].nome);
      }
    } catch (error) {
      console.error('Error fetching public settings:', error);
      setError('Impossibile caricare le figure professionali. Riprova più tardi.');
    }
  }, []);
  
  const fetchPrivacyPolicy = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/privacy-policy/active`);
      setPrivacyPolicy(response.data);
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
    }
  }, []);

  const checkPasswordStrength = (password) => {
    let score = 0;
    let message = '';
    let color = 'gray';

    if (password.length === 0) {
      setPasswordStrength({ score: 0, message: '', color: 'gray' });
      return;
    }

    // Check length
    if (password.length >= 8) score++;
    else {
      setPasswordStrength({ score: 0, message: 'Minimo 8 caratteri richiesti', color: 'red' });
      return;
    }

    // Check for number
    if (/\d/.test(password)) score++;
    else {
      setPasswordStrength({ score: 1, message: 'Aggiungi almeno un numero', color: 'orange' });
      return;
    }

    // Check for special character
    const specialChars = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/;
    if (specialChars.test(password)) score++;
    else {
      setPasswordStrength({ score: 2, message: 'Aggiungi un carattere speciale (!@#$% ecc.)', color: 'yellow' });
      return;
    }

    // All requirements met
    if (score === 3) {
      if (password.length >= 12) {
        setPasswordStrength({ score: 4, message: 'Password molto forte!', color: 'green' });
      } else {
        setPasswordStrength({ score: 3, message: 'Password forte', color: 'green' });
      }
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validazione consenso privacy
    if (!formData.privacy_consent) {
      setError('Devi accettare l\'informativa privacy per procedere');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting registration with data:', { ...formData, role: 'operatore' });
      const response = await axios.post(`${API}/auth/self-register`, {
        ...formData,
        role: 'operatore',
        privacy_consent_date: new Date().toISOString(),
        privacy_consent_version: privacyPolicy?.version || '1.0'
      });
      console.log('Registration successful:', response.data);
      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-cyan-50 to-sky-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registrazione Inviata!</h2>
            <p className="text-gray-600 mb-6">
              La tua richiesta di registrazione è stata inviata con successo. 
              L'attivazione dell'account richiede fino a 24 ore. Riceverai una notifica quando il tuo account sarà approvato.
            </p>
            <Button
              onClick={onBackToLogin}
              className="bg-gradient-to-r from-yellow-400 to-cyan-500 hover:from-yellow-500 hover:to-cyan-600 text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-cyan-50 to-sky-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Logo" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Registrazione Operatore</h1>
          <p className="text-gray-600 text-sm mt-2">Richiedi l'accesso come operatore</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>Compila i tuoi dati</CardTitle>
            <CardDescription>La registrazione richiede l'approvazione di un amministratore</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder="Scegli un username"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Crea una password"
                  minLength={8}
                />
                <div className="space-y-1">
                  <div className="flex gap-1 h-1">
                    <div className={`flex-1 rounded ${
                      passwordStrength.score >= 1 
                        ? passwordStrength.color === 'red' ? 'bg-red-500'
                          : passwordStrength.color === 'orange' ? 'bg-orange-500'
                          : passwordStrength.color === 'yellow' ? 'bg-yellow-500'
                          : 'bg-green-500'
                        : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded ${
                      passwordStrength.score >= 2 
                        ? passwordStrength.color === 'orange' ? 'bg-orange-500'
                          : passwordStrength.color === 'yellow' ? 'bg-yellow-500'
                          : 'bg-green-500'
                        : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded ${
                      passwordStrength.score >= 3 
                        ? passwordStrength.color === 'yellow' ? 'bg-yellow-500'
                          : 'bg-green-500'
                        : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex-1 rounded ${passwordStrength.score >= 4 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                  </div>
                  {passwordStrength.message && (
                    <p className={`text-xs ${
                      passwordStrength.color === 'red' ? 'text-red-600'
                        : passwordStrength.color === 'orange' ? 'text-orange-600'
                        : passwordStrength.color === 'yellow' ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>{passwordStrength.message}</p>
                  )}
                  <p className="text-xs text-gray-500">Richiesto: 8 caratteri, 1 numero, 1 carattere speciale</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="first_name">Nome *</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  placeholder="Il tuo nome"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Cognome *</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  placeholder="Il tuo cognome"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="esempio@email.com"
                />
                <p className="text-xs text-gray-500">Riceverai notifiche su questa email</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="codice_fiscale">Codice Fiscale *</Label>
                <Input
                  id="codice_fiscale"
                  placeholder="RSSMRA85M01H501Z"
                  value={formData.codice_fiscale}
                  onChange={(e) => {
                    const cf = e.target.value.toUpperCase().trim();
                    setFormData({ ...formData, codice_fiscale: cf });
                  }}
                  maxLength={16}
                  required
                  pattern="[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]"
                  title="Formato: 6 lettere, 2 cifre, 1 lettera, 2 cifre, 1 lettera, 3 cifre, 1 lettera"
                />
                <p className="text-xs text-gray-500">16 caratteri alfanumerici (es. RSSMRA85M01H501Z)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="figura_professionale">Figura Professionale *</Label>
                {!settings ? (
                  <div className="w-full h-10 px-3 rounded-md border border-gray-300 bg-gray-50 flex items-center text-sm text-gray-500">
                    Caricamento...
                  </div>
                ) : (
                  <select
                    id="figura_professionale"
                    value={formData.figura_professionale}
                    onChange={(e) => setFormData({ ...formData, figura_professionale: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                    required
                  >
                    {!formData.figura_professionale && (
                      <option value="">Seleziona una figura professionale</option>
                    )}
                    {settings.figure_professionali?.map((fig) => (
                      <option key={fig.id} value={fig.nome}>{fig.nome}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="asl_riferimento">ASL di Riferimento *</Label>
                <select
                  id="asl_riferimento"
                  value={formData.asl_riferimento}
                  onChange={(e) => setFormData({ ...formData, asl_riferimento: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white"
                  required
                >
                  <option value="Asl Pescara">Asl Pescara</option>
                  <option value="Asl Chieti">Asl Chieti</option>
                  <option value="Asl L'Aquila">Asl L'Aquila</option>
                  <option value="Asl Teramo">Asl Teramo</option>
                </select>
              </div>

              {/* Privacy Consent */}
              <div className="space-y-3 p-4 border-2 border-green-300 rounded-lg bg-green-50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="privacy_consent"
                    checked={formData.privacy_consent}
                    onChange={(e) => setFormData({ ...formData, privacy_consent: e.target.checked })}
                    className="mt-1 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500"
                    required
                  />
                  <div className="flex-1">
                    <Label htmlFor="privacy_consent" className="text-sm font-semibold text-green-900 cursor-pointer">
                      Ho letto e accetto l'informativa privacy *
                    </Label>
                    <p className="text-xs text-gray-700 mt-1">
                      {privacyPolicy ? (
                        <>
                          Versione {privacyPolicy.version} del {new Date(privacyPolicy.effective_date).toLocaleDateString('it-IT')}.{' '}
                          <button
                            type="button"
                            onClick={() => setShowPrivacyDialog(true)}
                            className="text-cyan-600 hover:text-cyan-700 underline font-medium"
                          >
                            Leggi l'informativa completa
                          </button>
                        </>
                      ) : (
                        'Caricamento informativa...'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBackToLogin}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Indietro
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-cyan-500 hover:from-yellow-500 hover:to-cyan-600 text-gray-900"
                >
                  {loading ? 'Invio...' : 'Registrati'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Button
            variant="link"
            onClick={onBackToLogin}
            className="text-gray-600 hover:text-gray-900"
          >
            Hai già un account? Accedi
          </Button>
        </div>
      </div>
      
      {/* Privacy Policy Dialog */}
      {showPrivacyDialog && privacyPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPrivacyDialog(false)}>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">{privacyPolicy.title}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Versione {privacyPolicy.version} - Aggiornata il {new Date(privacyPolicy.updated_at).toLocaleDateString('it-IT')}
              </p>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: privacyPolicy.content }}
              />
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPrivacyDialog(false)}
              >
                Chiudi
              </Button>
              <Button
                onClick={() => {
                  setFormData({ ...formData, privacy_consent: true });
                  setShowPrivacyDialog(false);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Accetto
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicRegister;
